import React from 'react';
import { Card } from '@/components/ui/card';
import { Receipt, TrendingDown, Clock, Wrench, Layers, Hammer } from 'lucide-react';
import type { LayerV2 } from '../../types';
import { useStratigraphyCosts } from '../../hooks/useStratigraphyCosts';
import { formatNumber } from '@/utils/format';
import { useWasteFactors } from '@/hooks/useWasteFactors';
import { useOrgProfile, PRICING_DEFAULTS } from '@/hooks/useOrgSettings';

interface CostsBreakdownProps {
  layers: LayerV2[];
  studSpacingMm: 300 | 400 | 600;
}

/**
 * Tab Costi: breakdown stile fattura del costo €/m² della stratigrafia.
 *
 * Per ogni materiale mostra: quantità · €/unit · costo riga · minuti posa.
 * Le viti sono in sezione dedicata, ognuna con tempo posa di fissaggio.
 * Il totale manodopera è la somma di tutti i minuti × cost_per_hour / 60.
 */
const CostsBreakdown: React.FC<CostsBreakdownProps> = ({ layers, studSpacingMm }) => {
  const { breakdown, costPerHour, isLoading } = useStratigraphyCosts(layers, studSpacingMm);
  const { wasteMap } = useWasteFactors();
  const { data: orgProfile } = useOrgProfile();
  const disposalPct = typeof orgProfile?.disposal_materials_pct === 'number'
    ? orgProfile.disposal_materials_pct
    : PRICING_DEFAULTS.disposal_materials_pct;

  /**
   * Sfrido STIMATO della parete, in €/m².
   *
   * Mostrato per trasparenza ma NON sommato al totale grande: quel numero
   * finisce su `stratigraphies.cost_per_sqm`, cioe` sulla parete di catalogo
   * CONDIVISA con tutte le aziende, mentre lo sfrido dipende dai default e
   * dagli override della tua organizzazione. Nel preventivo invece entra nel
   * costo, perche' li` la riga e` una copia tua.
   */
  /**
   * Sfrido e smaltimento STIMATI della parete, in €/m².
   *
   * Mostrati per trasparenza ma NON sommati al totale grande: quel numero
   * finisce su `stratigraphies.cost_per_sqm`, cioe` sulla parete di catalogo
   * CONDIVISA con tutte le aziende, mentre queste due percentuali dipendono
   * dai default e dagli override della singola organizzazione. Nel preventivo
   * invece entrano nel costo, perche' li` la riga e` una copia dell'org.
   *
   * Stesso PERIMETRO del calcolo del preventivo — materiali degli strati piu`
   * le viti, tasselli esclusi — altrimenti la stessa parete mostrerebbe qui un
   * numero e la` un altro.
   */
  const { sfrido, smaltimento } = React.useMemo(() => {
    let sf = 0;
    let sm = 0;
    const conta = (costo: number, mat: Record<string, unknown> | null | undefined) => {
      if (!(costo > 0)) return;
      const category = String(mat?.category ?? '');
      const w = mat?.waste_percentage;
      const wPct = (w !== null && w !== undefined && Number.isFinite(Number(w)))
        ? Number(w)
        : Number(wasteMap[category] ?? 0);
      const quota = wPct > 0 ? costo * (wPct / 100) : 0;
      sf += quota;
      const d = mat?.disposal_percentage;
      const dPct = (d !== null && d !== undefined && Number.isFinite(Number(d)))
        ? Number(d)
        : disposalPct;
      if (dPct > 0) sm += (costo + quota) * (dPct / 100);
    };
    for (const r of breakdown.rows) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      conta(r.rowCost, (layers[r.layerIdx] as any)?.material);
    }
    for (const r of breakdown.screwRows) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      conta(r.rowCost, (layers[r.layerIdx] as any)?.screwMaterial);
    }
    return { sfrido: sf, smaltimento: sm };
  }, [breakdown, layers, wasteMap, disposalPct]);

  if (layers.length === 0) {
    return (
      <Card className="p-12 text-center text-sm text-muted-foreground border-dashed min-h-[420px] flex items-center justify-center">
        <div>
          <Receipt className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
          <p>Aggiungi materiali per vedere il breakdown costi</p>
        </div>
      </Card>
    );
  }

  // Formato italiano (virgola decimale + punto migliaia). Il simbolo € resta
  // attaccato al numero: qui la tabella è compatta, lo spazio la allargherebbe.
  const fmtEur = (v: number) => `€${formatNumber(v)}`;
  const fmtMin = (v: number) => v < 1 ? `${formatNumber(v * 60, 0)}s` : `${formatNumber(v, 1)}'`;
  const fmtQty = (v: number, unit: string) => {
    if (unit === 'mq') return `${formatNumber(v)} m²`;
    if (unit === 'ml') return `${formatNumber(v)} ml`;
    if (unit === 'pz') return `${formatNumber(v, 0)} pz`;
    return `${formatNumber(v)} ${unit}`;
  };

  const categoryIcon = (cat: string) => {
    if (cat === 'board' || cat === 'ceiling_tile') return <Layers className="h-3.5 w-3.5 text-amber-600" />;
    if (cat === 'structure_frame' || cat === 'structure_guide') return <Wrench className="h-3.5 w-3.5 text-zinc-500" />;
    if (cat === 'insulation') return <Layers className="h-3.5 w-3.5 text-yellow-600" />;
    return <Layers className="h-3.5 w-3.5 text-zinc-400" />;
  };

  return (
    <Card className="p-0 overflow-hidden">
      {/* === SEZIONE MATERIALI === */}
      <div className="px-5 py-3">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
          <Layers className="h-3 w-3" />
          Materiali
        </div>
        {/* Header colonne */}
        <div className="grid grid-cols-12 gap-2 text-[9px] uppercase tracking-wider text-muted-foreground/60 pb-1.5 border-b border-zinc-100 mb-1.5">
          <div className="col-span-6">Materiale</div>
          <div className="col-span-2 text-right">Quantità</div>
          <div className="col-span-1 text-right">€/u</div>
          <div className="col-span-1 text-right" title="Tempo posa">⏱</div>
          <div className="col-span-2 text-right">Costo</div>
        </div>
        <div className="space-y-1.5">
          {breakdown.rows.length === 0 ? (
            <div className="text-xs text-muted-foreground italic py-2">Nessun materiale assegnato.</div>
          ) : (
            breakdown.rows.map((row) => (
              <div key={row.layerIdx} className="grid grid-cols-12 gap-2 items-baseline text-xs">
                <div className="col-span-6 flex items-baseline gap-1.5 min-w-0">
                  <span className="font-mono text-muted-foreground shrink-0">#{row.layerNum}</span>
                  <span className="shrink-0">{categoryIcon(row.category)}</span>
                  <div className="min-w-0">
                    <div className="font-medium text-zinc-800 truncate">{row.name}</div>
                    {row.notes && <div className="text-[10px] text-muted-foreground/80">{row.notes}</div>}
                  </div>
                </div>
                <div className="col-span-2 text-right font-mono text-muted-foreground">
                  {fmtQty(row.quantity, row.unit)}
                </div>
                <div className="col-span-1 text-right font-mono text-muted-foreground">
                  {fmtEur(row.unitPriceNet)}
                </div>
                <div className="col-span-1 text-right font-mono text-blue-700/80">
                  {row.rowMinutes > 0 ? fmtMin(row.rowMinutes) : '—'}
                </div>
                <div className="col-span-2 text-right font-mono font-semibold text-zinc-900">
                  {fmtEur(row.rowCost)}
                </div>
              </div>
            ))
          )}
        </div>
        <div className="flex justify-between items-baseline mt-3 pt-2 border-t border-zinc-100 text-xs">
          <span className="text-muted-foreground">Subtotale materiali</span>
          <span className="font-mono font-semibold text-zinc-900">{fmtEur(breakdown.subtotalMaterials)}</span>
        </div>
      </div>

      {/* === SEZIONE VITI (una riga per layer fissato) === */}
      {(breakdown.screwRows.length > 0 || breakdown.subtotalScrews > 0) && (
        <div className="px-5 py-3 border-t">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
            <Hammer className="h-3 w-3" />
            Viti
          </div>
          {breakdown.screwRows.length > 0 ? (
            <div className="space-y-1.5">
              {breakdown.screwRows.map((sr, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-baseline text-xs">
                  <div className="col-span-6 flex items-baseline gap-1.5 min-w-0">
                    <span className="font-mono text-muted-foreground shrink-0">#{sr.layerNum}</span>
                    <Hammer className="h-3.5 w-3.5 text-zinc-700 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-zinc-800 truncate">{sr.screwName}</div>
                      <div className="text-[10px] text-muted-foreground/80">
                        {formatNumber(sr.screwQuantityPerSqm, 1)} pz/m² · 5s/vite
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 text-right font-mono text-muted-foreground">
                    {formatNumber(sr.screwQuantityPerSqm, 0)} pz
                  </div>
                  <div className="col-span-1 text-right font-mono text-muted-foreground">
                    €{formatNumber(sr.pricePerPiece, 3)}
                  </div>
                  <div className="col-span-1 text-right font-mono text-blue-700/80">
                    {fmtMin(sr.rowMinutes)}
                  </div>
                  <div className="col-span-2 text-right font-mono font-semibold text-zinc-900">
                    {fmtEur(sr.rowCost)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex justify-between items-baseline text-xs">
              <span className="text-zinc-700">Fissaggio lastre (UNI 11424:2015)</span>
              <span className="font-mono font-semibold text-zinc-900">{fmtEur(breakdown.subtotalScrews)}</span>
            </div>
          )}
          <div className="flex justify-between items-baseline mt-2 pt-2 border-t border-zinc-100 text-xs">
            <span className="text-muted-foreground">Subtotale viti</span>
            <span className="font-mono font-semibold text-zinc-900">{fmtEur(breakdown.subtotalScrews)}</span>
          </div>
        </div>
      )}

      {/* === SEZIONE TASSELLI (fissaggio delle guide a pavimento e soffitto) === */}
      {breakdown.anchorRows.length > 0 && (
        <div className="px-5 py-3 border-t">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
            <Hammer className="h-3 w-3" />
            Fissaggio guide
          </div>
          <div className="space-y-1.5">
            {breakdown.anchorRows.map((ar, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-baseline text-xs">
                <div className="col-span-6 flex items-baseline gap-1.5 min-w-0">
                  <span className="font-mono text-muted-foreground shrink-0">#{ar.layerNum}</span>
                  <Hammer className="h-3.5 w-3.5 text-zinc-700 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-zinc-800 truncate">{ar.anchorName}</div>
                    <div className="text-[10px] text-muted-foreground/80">
                      {ar.anchorSupplier ? `${ar.anchorSupplier} · ` : ''}
                      {formatNumber(ar.anchorQuantityPerSqm)} pz/m² · uno ogni 50 cm, a pavimento e a soffitto
                    </div>
                  </div>
                </div>
                <div className="col-span-2 text-right font-mono text-muted-foreground">
                  {formatNumber(ar.anchorQuantityPerSqm)} pz
                </div>
                <div className="col-span-1 text-right font-mono text-muted-foreground">
                  €{formatNumber(ar.pricePerPiece, 3)}
                </div>
                <div className="col-span-1 text-right font-mono text-blue-700/80">
                  {fmtMin(ar.rowMinutes)}
                </div>
                <div className="col-span-2 text-right font-mono font-semibold text-zinc-900">
                  {fmtEur(ar.rowCost)}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-baseline mt-2 pt-2 border-t border-zinc-100 text-xs">
            <span className="text-muted-foreground">Subtotale fissaggi</span>
            <span className="font-mono font-semibold text-zinc-900">{fmtEur(breakdown.subtotalAnchors)}</span>
          </div>
        </div>
      )}

      {/* === MANODOPERA: somma di TUTTI i minuti dei materiali e viti === */}
      {breakdown.laborMinutes > 0 && (
        <div className="px-5 py-3 border-t bg-blue-50/30">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-blue-900/70 mb-2">
            <Clock className="h-3 w-3" />
            Manodopera
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between items-baseline">
              <span className="text-zinc-700">
                Tempo posa totale (somma minuti riga per riga)
              </span>
              <span className="font-mono font-semibold text-blue-900">
                {formatNumber(breakdown.laborMinutes, 1)} min
              </span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-muted-foreground">
                = <span className="font-mono">{formatNumber(breakdown.laborTime, 3)} h/m²</span> × {fmtEur(costPerHour)}/h
              </span>
              <span className="font-mono font-semibold text-zinc-900">{fmtEur(breakdown.laborCost)}</span>
            </div>
          </div>
        </div>
      )}

      {/* === TOTALE (riepilogo costi al m²) === */}
      <div className="px-5 py-4 border-t-2 border-zinc-200 bg-zinc-50">
        <div className="flex justify-between items-start gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide font-semibold text-zinc-700">
              Riepilogo costi al m²
            </div>
            <div className="text-[10px] text-muted-foreground/80 mt-0.5">
              Prezzi netti per la tua organizzazione (sconti già applicati)
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold font-mono text-zinc-900">{fmtEur(breakdown.totalCost)}</div>
            <div className="text-[10px] text-muted-foreground">€/m² · IVA esclusa</div>
          </div>
        </div>
        {(sfrido + smaltimento) > 0.005 && (
          <div className="mt-3 pt-2 border-t border-zinc-200 space-y-1">
            <div className="flex justify-between items-baseline text-xs">
              <span
                className="text-muted-foreground cursor-help"
                title="Materiale in più che acquisti per i ritagli. Percentuale per categoria (Impostazioni → Sfridi) o del singolo materiale."
              >
                Sfrido stimato
              </span>
              <span className="font-mono text-amber-800">+ {fmtEur(sfrido)}</span>
            </div>
            <div className="flex justify-between items-baseline text-xs">
              <span
                className="text-muted-foreground cursor-help"
                title="Oneri di discarica sul costo dei materiali, sfrido compreso. Percentuale dell'organizzazione (Organizzazione → Smaltimento) o del singolo materiale."
              >
                Smaltimento stimato
              </span>
              <span className="font-mono text-amber-800">+ {fmtEur(smaltimento)}</span>
            </div>
            <div className="flex justify-between items-baseline text-xs pt-1 border-t border-zinc-200/70">
              <span className="text-muted-foreground">Costo con sfrido e smaltimento</span>
              <span className="font-mono font-semibold text-zinc-900">
                {fmtEur(breakdown.totalCost + sfrido + smaltimento)}
              </span>
            </div>
            <div className="text-[10px] text-muted-foreground/80 leading-tight">
              È questo il costo che troverai sulla riga di preventivo. Qui resta fuori dal
              totale grande: questa parete è di catalogo e il suo €/m² è condiviso con le
              altre aziende, mentre queste due percentuali sono della tua.
            </div>
          </div>
        )}
      </div>

      {/* === RISPARMIO APPLICATO === */}
      {breakdown.totalSavings > 0.01 && (
        <div className="px-5 py-3 bg-emerald-50/70 border-t border-emerald-200/50 flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-emerald-700 shrink-0" />
          <div className="flex-1 text-xs">
            <span className="font-semibold text-emerald-900">Risparmio sconti applicati: </span>
            <span className="font-mono font-bold text-emerald-700">{fmtEur(breakdown.totalSavings)}/m²</span>
            <div className="text-[10px] text-emerald-700/80 mt-0.5">
              Differenza tra prezzo di listino e prezzo netto della tua organizzazione.
            </div>
          </div>
        </div>
      )}

      {/* === LOADING === */}
      {isLoading && (
        <div className="px-5 py-2 text-[10px] text-muted-foreground italic border-t">
          Caricamento costo orario manodopera…
        </div>
      )}
    </Card>
  );
};

export default CostsBreakdown;
